<template>
  <main class="w-full">
    <section class="w-full py-24 px-6 lg:px-24 bg-grey-dark-900">
      <div class="flex flex-col lg:flex-row items-center text-center lg:text-left gap-14">
        <div class="flex flex-col gap-4 md:gap-10 max-w-3xl">
          <p class="text-xl font-semibold w-full">BOOST</p>
          <h1 class="text-5xl lg:text-7xl font-semibold text-grey-dark-50 w-full">Save smarter, earn more</h1>

          <p class="text-xl font-normal text-grey-dark-300 w-full">LayUp's new savings and rewards platform is here.</p>
          <div class="flex flex-col-reverse sm:flex-row items-center justify-center lg:justify-start gap-4 mt-6">
            <router-link :to="{ name: 'business-enquiry' }">
              <Button variant="outline-light" size="2xl" class="w-full sm:w-auto py-6"> Talk to sales </Button>
            </router-link>
          </div>
        </div>
        <AnimationComponent>
          <img :src="boostProductImage" alt="Boost Product Image" class="w-full h-full object-cover" />
        </AnimationComponent>
      </div>
    </section>
    <Separator />
    <section class="w-full py-24 px-6 lg:px-24">
      <AnimationComponent>
        <div class="flex flex-col items-center justify-center text-center gap-10">
          <HeadingWithIcon
            :icon="getIconComponent('Zap')"
            title="Easy setup with Shopify"
            description="Seamlessly connect your brand through our Shopify plugin."
          />
          <img
            :src="boostConnectionImage"
            alt="Seamlessly connect your brand through our Shopify plugin."
            class="w-150 h-60"
          />
          <div class="w-full max-w-3xl">
            <NumberCounter class="text-boost-500" :counters="revenueCounters" />
          </div>
        </div>
      </AnimationComponent>
    </section>
    <Separator />
    <FeaturesSection
      :features="features"
      title="What is BOOST?"
      description="BOOST is a savings-powered rewards platform that helps convert window shoppers into paying customers, driving down CAC and increasing AOV at checkout."
      :items-per-row="4"
    />
    <TitleFeaturesSection
      :features="smartInstallmentFeatures"
      title="How BOOST works for you"
      description="BOOST rewards intent, not just clicks."
    />
    <section class="w-full pt-24 px-6 lg:px-24">
      <AnimationComponent>
        <div class="flex flex-col items-center justify-center text-center gap-10 mb-12">
          <h2 class="max-w-3xl text-4xl md:text-5xl font-semibold text-white">Easy integration</h2>
          <p class="max-w-3xl text-lg md:text-xl font-light text-grey-dark-300">
            Add LayUp's plugin to your e-commerce store or let us include you in our store directory.
          </p>
        </div>
      </AnimationComponent>
      <AnimationComponent>
        <div class="flex flex-col items-center justify-center text-center gap-10">
          <img
            :src="integrationImage"
            alt="Automate tracking, reconciliation and reporting"
            class="w-full h-full rounded-t-2xl border-6 border-grey-dark-700 border-b-0 object-cover"
          />
        </div>
      </AnimationComponent>
    </section>
    <PaymentMethodsSection
      title="Payment methods"
      description="Offer a seamless experience where customers can manage their subscriptions directly from their dashboard."
      link-to-page="business-pricing-boost"
    />
    <TitleFeaturesSection
      :features="cloudPaymentFeatures"
      title="How it works for your customers"
      description="Getting started is simple:"
    />
    <ApiFeatures />
    <CtaSection
      title="Get started with Boost"
      description="Low transaction fees and flexible settlements, all without a monthly contract."
      color="grey-dark-900"
    />
  </main>
</template>

<script setup lang="ts">
import integrationImage from '@/assets/images/business/boost-easy-integration.webp';
import boostConnectionImage from '@/assets/images/business/boost-easy-setup.webp';
import boostProductImage from '@/assets/images/business/boost-product-image.webp';
import AnimationComponent from '@/components/AnimationComponent.vue';
import CtaSection from '@/components/business/CtaSection.vue';
import FeaturesSection from '@/components/business/FeaturesSection.vue';
import PaymentMethodsSection from '@/components/business/PaymentMethodsSection.vue';
import TitleFeaturesSection from '@/components/business/TitleFeaturesSection.vue';
import { Button } from '@/components/ui/button';
import Separator from '@/components/ui/separator/SeparatorComponent.vue';
import ApiFeatures from '../../components/business/FeatureApi.vue';
import HeadingWithIcon from '../../components/business/HeadingWithIcon.vue';
import NumberCounter from '../../components/NumberCounter.vue';
import { getIconComponent } from '../../utils/icons';

const revenueCounters = [
  {
    number: 79,
    unit: '%',
    description: 'of customers drop off before they get to checkout.',
  },
  {
    number: 36,
    unit: '%',
    description: 'More brands see increased revenue via rewards and loyalty programs.',
  },
  {
    number: 90,
    unit: '%',
    description: 'of loyalty programme owners reported positive ROI.',
  },
];

const features = [
  {
    icon: 'Percent',
    title: 'Boost sales',
    description: 'Keep customers engaged at checkout with a save-now incentive.',
  },
  {
    icon: 'TrendingUp',
    title: 'Increase conversions',
    description: 'BOOST brings high-intent buyers back to you',
  },
  {
    icon: 'CreditCard',
    title: 'Improve cash flow',
    description: 'Lock in future sales with upfront deposits.',
  },
  {
    icon: 'UsersRound',
    title: 'Enhance loyalty',
    description: 'Keep customers engaged and committed to their purchase.',
  },
];

const smartInstallmentFeatures = [
  {
    icon: 'Globe',
    title: '1. Install the Shopify plugin',
    description:
      'Add the BOOST plugin to your Shopify store and place the payment widget anywhere in your sales funnel.',
  },
  {
    icon: 'Puzzle',
    title: '2. Set savings threshold',
    description: 'Set the savings threshold you want your customers to reach to get rewarded.',
  },
  {
    icon: 'Settings',
    title: '3. Set reward threshold',
    description: 'Set the reward threshold customers unlock once they reach their savings goal.',
  },
  {
    icon: 'TrendingUp',
    title: '4. Reward unlocks at completion',
    description: 'When the customer reaches their goal, BOOST automatically issues the reward, and you get settled.',
  },
];

const cloudPaymentFeatures = [
  {
    icon: 'UserPen',
    title: 'Sign up for free',
    description: 'Simply create an account and opt in for LayUp BOOST.',
  },
  {
    icon: 'Target',
    title: 'Set goal amount',
    description: 'Activate the savings plan by setting the goal and down payment amount.',
  },
  {
    icon: 'PiggyBank',
    title: 'Start saving',
    description: 'Customers save over time and get notified throughout the journey.',
  },
  {
    icon: 'Award',
    title: 'Receive a BOOST Reward',
    description: 'Upon reaching your goal, you will receive a BOOST Reward added to your savings amount.',
  },
  {
    icon: 'Gift',
    title: 'Redeem voucher',
    description:
      'Customers receive a voucher code to complete their purchases on your website at checkout and buy big.',
  },
];
</script>
